import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/confirm-dialog'
import { Loader2, Link2, Search, FileText, ExternalLink, Unplug, BarChart3, RefreshCw, ArrowUp, ArrowDown, Minus, History, ChevronDown, ChevronRight } from 'lucide-react'
import {
  useBingStatus,
  useConnectBing,
  useDisconnectBing,
  useBingQueries,
  useBingPages,
  useBingBacklinks,
  useBingTraffic,
} from '../hooks/useBing'
import {
  useBingSnapshotQueries,
  useBingSnapshotTimeseries,
  useTakeBingSnapshot,
} from '../hooks/useBingSnapshots'
import { rankBadgeColor } from '@/client/lib/colors'

interface BingWebmasterTabProps {
  siteId: string
  domain: string
  tier?: string
}

function BingConnectForm({ siteId, domain }: { siteId: string; domain: string }) {
  const connect = useConnectBing(siteId)
  const [apiKey, setApiKey] = useState('')
  const [siteUrl, setSiteUrl] = useState(`https://${domain}/`)

  return (
    <Card>
      <CardContent className="py-6">
        <div className="text-center mb-6">
          <Search className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <h3 className="text-lg font-semibold">Connect Bing Webmaster Tools</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Get exact keyword data (not anonymised like GSC), backlink counts, and 6 months of search history from Bing.
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bing-api-key">Bing Webmaster API Key</Label>
            <Input
              id="bing-api-key"
              type="password"
              placeholder="Enter your Bing API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Get your API key from{' '}
              <a
                href="https://www.bing.com/webmasters/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Bing Webmaster Tools
                <ExternalLink className="inline h-3 w-3 ml-0.5" />
              </a>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bing-site-url">Site URL (as registered in Bing)</Label>
            <Input
              id="bing-site-url"
              placeholder="https://example.com/"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
            />
          </div>

          {connect.isError && (
            <p className="text-sm text-destructive">
              {(connect.error as Error)?.message || 'Failed to connect. Check your API key and try again.'}
            </p>
          )}

          <Button
            className="w-full"
            onClick={() => connect.mutate({ apiKey, siteUrl })}
            disabled={connect.isPending || !apiKey || !siteUrl}
          >
            {connect.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Link2 className="mr-2 h-4 w-4" />
            )}
            Connect Bing
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Position change indicator ─────────────────────────────────

function PositionChange({ change }: { change: number | null }) {
  if (change === null) return <span className="text-muted-foreground">-</span>
  if (change === 0) return <Minus className="h-3 w-3 text-muted-foreground inline" />
  // Lower position number = better ranking, so negative change = improvement
  if (change < 0) {
    return (
      <span className="text-green-600 dark:text-green-400 inline-flex items-center gap-0.5">
        <ArrowUp className="h-3 w-3" />
        {Math.abs(change).toFixed(1)}
      </span>
    )
  }
  return (
    <span className="text-red-600 dark:text-red-400 inline-flex items-center gap-0.5">
      <ArrowDown className="h-3 w-3" />
      {change.toFixed(1)}
    </span>
  )
}

function PositionBadge({ position }: { position: number }) {
  return <Badge className={`text-xs tabular-nums ${rankBadgeColor(position)}`}>{position.toFixed(1)}</Badge>
}

// ── Expanded row with timeseries chart ─────────────────────────

function QueryTimeseries({ siteId, query }: { siteId: string; query: string }) {
  const { data, isLoading } = useBingSnapshotTimeseries(siteId, query)
  if (isLoading) return <Skeleton className="h-24 w-full" />
  if (!data?.timeseries?.length) return <p className="text-xs text-muted-foreground py-4">No historical data yet.</p>

  const ts = data.timeseries
  const maxClicks = Math.max(...ts.map(t => t.clicks), 1)

  return (
    <div className="py-3 px-2">
      <div className="grid grid-cols-2 gap-4">
        {/* Clicks chart */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Clicks</p>
          <div className="h-16 flex items-end gap-px">
            {ts.map((t, i) => (
              <div
                key={i}
                className="flex-1 bg-blue-500/70 rounded-t-sm min-w-[2px]"
                style={{ height: `${(t.clicks / maxClicks) * 100}%` }}
                title={`${t.snapshot_date}: ${t.clicks} clicks`}
              />
            ))}
          </div>
        </div>
        {/* Position chart */}
        <div>
          <p className="text-xs text-muted-foreground mb-1">Avg Position</p>
          <div className="h-16 flex items-end gap-px">
            {ts.map((t, i) => {
              // Invert: lower position = taller bar (better)
              const maxPos = Math.max(...ts.map(p => p.avg_impression_position), 1)
              const height = maxPos > 0 ? ((maxPos - t.avg_impression_position + 1) / (maxPos + 1)) * 100 : 0
              return (
                <div
                  key={i}
                  className="flex-1 bg-green-500/70 rounded-t-sm min-w-[2px]"
                  style={{ height: `${Math.max(height, 2)}%` }}
                  title={`${t.snapshot_date}: position ${t.avg_impression_position.toFixed(1)}`}
                />
              )
            })}
          </div>
        </div>
      </div>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        <span>{ts[0]?.snapshot_date}</span>
        <span>{ts[ts.length - 1]?.snapshot_date}</span>
      </div>
    </div>
  )
}

// ── Rankings sub-tab ──────────────────────────────────────────

function BingRankingsView({ siteId, tier }: { siteId: string; tier?: string }) {
  const { data, isLoading } = useBingSnapshotQueries(siteId)
  const takeSnapshot = useTakeBingSnapshot(siteId)
  const [expandedQuery, setExpandedQuery] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'impressions' | 'clicks' | 'position'>('impressions')

  if (isLoading) return <Skeleton className="h-48 w-full" />

  const queries = data?.queries ?? []

  if (!queries.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <History className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            No Bing ranking snapshots yet. Snapshots capture weekly query performance data from Bing Webmaster Tools.
          </p>
          {tier === 'managed' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => takeSnapshot.mutate()}
              disabled={takeSnapshot.isPending}
            >
              {takeSnapshot.isPending ? 'Taking snapshot...' : 'Take Snapshot Now'}
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  const sorted = [...queries].sort((a, b) => {
    if (sortBy === 'clicks') return b.clicks - a.clicks
    if (sortBy === 'position') return a.avgImpressionPosition - b.avgImpressionPosition
    return b.impressions - a.impressions
  })

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      {tier === 'managed' && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => takeSnapshot.mutate()}
            disabled={takeSnapshot.isPending}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${takeSnapshot.isPending ? 'animate-spin' : ''}`} />
            {takeSnapshot.isPending ? 'Snapshotting...' : 'Snapshot Now'}
          </Button>
          {data?.snapshotDate && (
            <span className="ml-auto text-xs text-muted-foreground">
              Latest: {data.snapshotDate}
            </span>
          )}
        </div>
      )}

      {takeSnapshot.isSuccess && (
        <div className="text-sm text-green-600 dark:text-green-400">
          Snapshot captured {takeSnapshot.data?.queriesStored ?? 0} queries.
        </div>
      )}

      {/* Rankings table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Bing Query Rankings
              <Badge variant="outline" className="text-xs">{queries.length} queries</Badge>
            </CardTitle>
            <div className="flex gap-1">
              {(['impressions', 'clicks', 'position'] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={sortBy === s ? 'default' : 'outline'}
                  className="h-7 px-2 text-xs capitalize"
                  onClick={() => setSortBy(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 font-medium w-6"></th>
                  <th className="pb-2 font-medium">Query</th>
                  <th className="pb-2 font-medium text-right">Clicks</th>
                  <th className="pb-2 font-medium text-right">Impressions</th>
                  <th className="pb-2 font-medium text-right">Position</th>
                  <th className="pb-2 font-medium text-right">Change</th>
                </tr>
              </thead>
              <tbody>
                {sorted.slice(0, 100).map((q) => (
                  <>
                    <tr
                      key={q.query}
                      className="border-b border-border/50 last:border-0 cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedQuery(expandedQuery === q.query ? null : q.query)}
                    >
                      <td className="py-2">
                        {expandedQuery === q.query
                          ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                          : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                      </td>
                      <td className="py-2 max-w-xs truncate" title={q.query}>{q.query}</td>
                      <td className="py-2 text-right tabular-nums">{q.clicks.toLocaleString()}</td>
                      <td className="py-2 text-right tabular-nums">{q.impressions.toLocaleString()}</td>
                      <td className="py-2 text-right"><PositionBadge position={q.avgImpressionPosition} /></td>
                      <td className="py-2 text-right"><PositionChange change={q.positionChange} /></td>
                    </tr>
                    {expandedQuery === q.query && (
                      <tr key={`${q.query}-chart`}>
                        <td colSpan={6} className="bg-muted/30 border-b">
                          <QueryTimeseries siteId={siteId} query={q.query} />
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
            {sorted.length > 100 && (
              <p className="text-xs text-muted-foreground text-center pt-2">
                Showing 100 of {sorted.length} queries.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ── Main data view with sub-tabs ──────────────────────────────

function BingDataView({ siteId, tier }: { siteId: string; tier?: string }) {
  const [subTab, setSubTab] = useState<'overview' | 'rankings'>('overview')
  const disconnect = useDisconnectBing(siteId)
  const confirmDisconnect = useConfirmDialog({
    onConfirm: async () => { await disconnect.mutateAsync() },
  })
  const { data: queriesData, isLoading: queriesLoading } = useBingQueries(siteId)
  const { data: pagesData, isLoading: pagesLoading } = useBingPages(siteId)
  const { data: backlinksData, isLoading: backlinksLoading } = useBingBacklinks(siteId)
  const { data: trafficData, isLoading: trafficLoading } = useBingTraffic(siteId)

  const queries = queriesData?.queries ?? []
  const pages = pagesData?.pages ?? []
  const traffic = trafficData?.traffic ?? []

  // Calculate totals from queries
  const totalClicks = queries.reduce((sum, q) => sum + q.clicks, 0)
  const totalImpressions = queries.reduce((sum, q) => sum + q.impressions, 0)

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Total Clicks</p>
            <p className="text-2xl font-bold">{queriesLoading ? '-' : totalClicks.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Impressions</p>
            <p className="text-2xl font-bold">{queriesLoading ? '-' : totalImpressions.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Inbound Links</p>
            <p className="text-2xl font-bold">
              {backlinksLoading ? '-' : (backlinksData?.inboundLinks ?? 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Indexed Pages</p>
            <p className="text-2xl font-bold">
              {backlinksLoading ? '-' : (backlinksData?.indexedPages ?? 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Sub-tab selector */}
      <div className="flex items-center gap-1 border-b">
        {([
          { key: 'overview' as const, label: 'Overview' },
          { key: 'rankings' as const, label: 'Rankings' },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              subTab === tab.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'overview' && (
        <>
          {/* Traffic chart */}
          {!trafficLoading && traffic.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Bing Traffic (Last 6 Months)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-32 flex items-end gap-px">
                  {traffic.slice(-60).map((row, i) => {
                    const maxClicks = Math.max(...traffic.slice(-60).map((r) => r.clicks), 1)
                    return (
                      <div
                        key={i}
                        className="flex-1 bg-blue-500/70 rounded-t-sm min-w-[2px]"
                        style={{ height: `${(row.clicks / maxClicks) * 100}%` }}
                        title={`${row.date}: ${row.clicks} clicks, ${row.impressions} impressions`}
                      />
                    )
                  })}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>{traffic.length > 60 ? traffic[traffic.length - 60]?.date?.slice(5) : traffic[0]?.date?.slice(5)}</span>
                  <span>{traffic[traffic.length - 1]?.date?.slice(5)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Queries table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="h-4 w-4" />
                Top Bing Queries
                <span className="text-muted-foreground font-normal text-sm">({queries.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {queriesLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : queries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No query data available from Bing.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 font-medium">Query</th>
                        <th className="pb-2 font-medium text-right">Clicks</th>
                        <th className="pb-2 font-medium text-right">Impressions</th>
                        <th className="pb-2 font-medium text-right">Avg Position</th>
                      </tr>
                    </thead>
                    <tbody>
                      {queries.slice(0, 50).map((q, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0">
                          <td className="py-2 max-w-xs truncate" title={q.query}>{q.query}</td>
                          <td className="py-2 text-right tabular-nums">{q.clicks.toLocaleString()}</td>
                          <td className="py-2 text-right tabular-nums">{q.impressions.toLocaleString()}</td>
                          <td className="py-2 text-right tabular-nums">{q.avgImpressionPosition.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {queries.length > 50 && (
                    <p className="text-xs text-muted-foreground text-center pt-2">
                      Showing 50 of {queries.length} queries.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pages table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Top Pages
                <span className="text-muted-foreground font-normal text-sm">({pages.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pagesLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : pages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No page data available from Bing.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="pb-2 font-medium">Page</th>
                        <th className="pb-2 font-medium text-right">Clicks</th>
                        <th className="pb-2 font-medium text-right">Impressions</th>
                        <th className="pb-2 font-medium text-right">Avg Position</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pages.slice(0, 50).map((p, i) => {
                        let displayUrl = p.url
                        try { displayUrl = new URL(p.url).pathname } catch { /* use full url */ }
                        return (
                          <tr key={i} className="border-b border-border/50 last:border-0">
                            <td className="py-2 max-w-xs truncate font-mono text-xs" title={p.url}>{displayUrl}</td>
                            <td className="py-2 text-right tabular-nums">{p.clicks.toLocaleString()}</td>
                            <td className="py-2 text-right tabular-nums">{p.impressions.toLocaleString()}</td>
                            <td className="py-2 text-right tabular-nums">{p.avgImpressionPosition.toFixed(1)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {subTab === 'rankings' && (
        <BingRankingsView siteId={siteId} tier={tier} />
      )}

      {/* Disconnect */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={() => confirmDisconnect.openDialog(undefined as void)}
          disabled={disconnect.isPending}
        >
          {disconnect.isPending ? (
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
          ) : (
            <Unplug className="mr-2 h-3 w-3" />
          )}
          Disconnect Bing
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDisconnect.open}
        onOpenChange={confirmDisconnect.setOpen}
        title="Disconnect Bing Webmaster Tools?"
        description="This will remove the API key for this site. You'll need to re-enter it to reconnect."
        confirmLabel="Disconnect"
        isLoading={confirmDisconnect.isLoading}
        onConfirm={confirmDisconnect.handleConfirm}
      />
    </div>
  )
}

export function BingWebmasterTab({ siteId, domain, tier }: BingWebmasterTabProps) {
  const { data, isLoading } = useBingStatus(siteId)

  if (isLoading) return <Skeleton className="h-48 w-full" />

  if (!data?.connected) {
    return <BingConnectForm siteId={siteId} domain={domain} />
  }

  return <BingDataView siteId={siteId} tier={tier} />
}
